'use client';

import { Box, Grid } from '@mui/material';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { FilteredReviewContentSection } from '@repo/supergraph/review/bankingPage/types';
import { CtaButton } from '@repo/ui/CtaButton';
import { LogoWrapper } from '../../OverviewPanel/LogoWrapper';
import { StarRating } from '../../shared/StarRating';
import { Drivers } from './components/Drivers';
import { TitleRow } from './components/TitleRow';

type Props = {
  currentPageInstitutionId?: string;
  displayRatingBesideName?: boolean;
  index: number;
  impressionSection?: string;
  productSection: FilteredReviewContentSection;
  isComparisonOffer?: boolean;
};

export const ProductCardDetails = ({
  currentPageInstitutionId,
  displayRatingBesideName = false,
  index,
  impressionSection = 'review_product_card',
  productSection,
  isComparisonOffer = false,
}: Props) => {
  const { marketplaceEntity, drivers } = productSection;

  if (!marketplaceEntity) return null;

  const { displayName, image, starRating } = marketplaceEntity;
  const overrideCta = marketplaceEntity.ctaOverride ?? marketplaceEntity.cta;
  const safeDrivers = drivers ?? [];
  const shouldShowReviewLink = Boolean(
    currentPageInstitutionId && marketplaceEntity.institution?.id !== currentPageInstitutionId,
  );
  const shouldShowCta =
    isComparisonOffer || overrideCta?.type === EntityPlacementCtaTypeEnum.ApplyNow;

  const analyticsPayload = {
    index,
    institutionId: marketplaceEntity.institution?.id,
    offerId: marketplaceEntity.id,
    productId: marketplaceEntity.product?.id,
    productType: marketplaceEntity.productType,
    monetizable: marketplaceEntity.monetizable,
  };

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={marketplaceEntity.name}
      productType={marketplaceEntity.productType}
      level="offer"
      institutionId={marketplaceEntity.institution?.id}
      institutionName={marketplaceEntity.institution?.name}
      productId={marketplaceEntity.product?.id}
      productName={marketplaceEntity.product?.name}
      offerId={marketplaceEntity.id}
      offerName={marketplaceEntity.name}
      monetizable={marketplaceEntity.monetizable}
      impressionSection={impressionSection}
      impressionPosition={index}
      additionalAttributes={{
        page_type: 'review',
      }}
    >
      <Grid
        id={marketplaceEntity.id}
        container
        sx={{
          paddingY: '1.5rem',
          display: 'grid',
          gridTemplateAreas: {
            xs: `
              "title title"
              "image image"
              ${shouldShowCta ? `"cta cta"` : ''}
              "drivers drivers"
            `,
            md: `
              "image titleAndDrivers titleAndDrivers"
              ${
                shouldShowCta
                  ? `"cta titleAndDrivers titleAndDrivers"`
                  : `"image titleAndDrivers titleAndDrivers"`
              }
            `,
          },
          gridTemplateColumns: {
            xs: 'auto 1fr',
            md: '1fr 1fr 1fr',
          },
          gridTemplateRows: {
            xs: `auto auto ${shouldShowCta ? 'auto' : ''} auto`,
            md: 'auto auto',
          },
          alignItems: 'center',
        }}
      >
        {/* Mobile title */}
        <Box
          sx={{
            gridArea: { xs: 'title' },
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            marginBottom: { xs: '32px', md: '1rem' },
            width: { xs: 'fit-content', md: '100%' },
            justifySelf: { xs: 'center', md: 'auto' },
            justifyContent: { xs: 'center', md: 'space-between' },
          }}
        >
          <TitleRow
            displayName={displayName}
            overrideCta={overrideCta}
            analyticsPayload={analyticsPayload}
            displayRatingBesideName={displayRatingBesideName}
            starRating={starRating}
            shouldShowReviewLink={shouldShowReviewLink}
            reviewLink={marketplaceEntity.reviewLink}
            shouldShowCta={shouldShowCta}
          />
        </Box>

        {/* Image + mobile rating block */}
        <Box
          sx={{
            gridArea: 'image',
            display: 'flex',
            flexDirection: 'row',
            gap: '1.5rem',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            marginBottom: shouldShowCta ? '1rem' : 0,
            paddingLeft: { md: '4px' }, // Prevent focus outline from being cutoff
          }}
        >
          {image ? (
            <LogoWrapper
              source={image.source}
              overrideCta={overrideCta}
              analyticsPayload={analyticsPayload}
              imageMaxWidth="180px"
              shouldShowCta={shouldShowCta}
            />
          ) : null}

          <StarRating
            value={starRating}
            layout="row"
            withBackground
            showLabel
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'flex-start',
            }}
          />
        </Box>

        {/* CTA */}
        {shouldShowCta ? (
          <Box
            sx={{
              gridArea: 'cta',
              width: { xs: '100%', md: '180px' },
              marginBottom: { xs: '32px', md: 0 },
              paddingLeft: { md: '4px' }, // Prevent focus outline from being cutoff
            }}
          >
            <CtaButton
              cta={overrideCta}
              analyticsPayload={analyticsPayload}
              subtextStylesOverride={{
                marginTop: 2,
                fontSize: 11,
                display: 'block',
                color: 'black',
              }}
              fullWidth
            />
          </Box>
        ) : null}

        {/* Drivers (mobile) */}
        <Box
          sx={{
            gridArea: { xs: 'drivers' },
            display: { xs: 'flex', md: 'none' },
            width: '100%',
            marginTop: shouldShowCta ? 0 : '32px',
          }}
        >
          <Drivers drivers={safeDrivers} index={index} />
        </Box>

        {/* Desktop title + drivers */}
        <Box
          sx={{
            gridArea: { md: 'titleAndDrivers' },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '1rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <TitleRow
              displayName={displayName}
              overrideCta={overrideCta}
              analyticsPayload={analyticsPayload}
              displayRatingBesideName={displayRatingBesideName}
              starRating={starRating}
              shouldShowReviewLink={shouldShowReviewLink}
              reviewLink={marketplaceEntity.reviewLink}
              shouldShowCta={shouldShowCta}
            />
          </Box>

          <Drivers drivers={safeDrivers} index={index} />
        </Box>
      </Grid>
    </AnalyticsSkuViewed>
  );
};
